/* ===== Events Page ===== */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { CalendarEvent } from '@zetyar/domain'
import {
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Info,
  List,
  Map,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Users,
  Video,
  Check,
} from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webEventRepository } from '@/infrastructure/repositories/events'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'
import { EventPhotosSection } from './EventPhotosSection'
import { getSupabaseClient } from '@/infrastructure/supabase/app'

interface Attendee {
  id: string
  status: string
  checked_in_at: string | null
  membership: {
    id: string
    display_name: string
  } | null
}

type ViewMode = 'list' | 'month' | 'week'

/* ===== Chilean Date & Time Formatters (24h / America/Santiago) ===== */
function formatChileanDate(dateStr: string | Date): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
  return d.toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'America/Santiago',
  })
}

function formatChileanTime(dateStr: string | Date): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
  const time = d.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Santiago',
  })
  return `${time} hrs`
}

function formatChileanDateTime(dateStr: string | Date): string {
  return `${formatChileanDate(dateStr)}, ${formatChileanTime(dateStr)}`
}

function toInputDateTimeLocal(dateStr?: string | Date | null): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => n.toString().padStart(2, '0')
  const year = d.getFullYear()
  const month = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const hours = pad(d.getHours())
  const minutes = pad(d.getMinutes())
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

function getMapEmbedUrl(location?: string | null, virtualUrl?: string | null): string {
  if (virtualUrl) {
    if (virtualUrl.includes('/maps/embed')) {
      return virtualUrl
    }
    const coordMatch =
      virtualUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) ||
      virtualUrl.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/)
    if (coordMatch) {
      return `https://maps.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&z=15&output=embed`
    }
    const placeMatch = virtualUrl.match(/\/place\/([^/@?]+)/)
    if (placeMatch) {
      return `https://maps.google.com/maps?q=${placeMatch[1]}&z=15&output=embed`
    }
  }

  const query =
    location?.trim() ||
    (virtualUrl && !virtualUrl.startsWith('http') ? virtualUrl.trim() : '')
  if (query) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`
  }

  return `https://maps.google.com/maps?q=Chile&z=12&output=embed`
}

function getExternalMapUrl(location?: string | null, virtualUrl?: string | null): string {
  if (virtualUrl && virtualUrl.startsWith('http')) {
    return virtualUrl
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location || virtualUrl || '')}`
}

function getMonthCalendarDays(currentDate: Date): Date[] {
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)

  // Monday = 0, Sunday = 6
  const startDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7

  const days: Date[] = []
  for (let i = startDayOfWeek; i > 0; i--) {
    days.push(new Date(year, month, 1 - i))
  }
  for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
    days.push(new Date(year, month, i))
  }
  const remaining = (7 - (days.length % 7)) % 7
  for (let i = 1; i <= remaining; i++) {
    days.push(new Date(year, month + 1, i))
  }
  return days
}

function getWeekDays(currentDate: Date): Date[] {
  const date = new Date(currentDate)
  const dayOfWeek = (date.getDay() + 6) % 7
  const monday = new Date(date)
  monday.setDate(date.getDate() - dayOfWeek)

  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    days.push(d)
  }
  return days
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

const WEEKDAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
const WEEKDAY_FULL_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

export function EventsPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const { guard, reauthModal } = useReauthGuard()
  const [searchParams, setSearchParams] = useSearchParams()

  const canCreate = profile?.isSuperAdmin || permissions.includes('events.create')
  const canEdit =
    profile?.isSuperAdmin ||
    permissions.includes('events.manage') ||
    permissions.includes('events.create') ||
    (currentMembership?.roles.includes('moderator') ?? false)
  const canDelete = profile?.isSuperAdmin || permissions.includes('events.manage')
  // Photos follow events.create / events.manage, the same roles the server accepts (0040).
  const canManagePhotos =
    Boolean(profile?.isSuperAdmin) || permissions.includes('events.manage') || permissions.includes('events.create')

  const photoContext = useMemo(() => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }, [currentMembership?.id, currentOrganization, profile, user])

  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [registeringId, setRegisteringId] = useState<string | null>(null)
  const [registeredIds, setRegisteredIds] = useState<Set<string>>(new Set())

  // View mode and calendar date navigation
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [calendarDate, setCalendarDate] = useState<Date>(new Date())

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null)
  const [selectedEventForDetail, setSelectedEventForDetail] = useState<CalendarEvent | null>(null)
  const [expandedMapEventId, setExpandedMapEventId] = useState<string | null>(null)
  const [viewingAttendeesEvent, setViewingAttendeesEvent] = useState<CalendarEvent | null>(null)
  const [attendees, setAttendees] = useState<Attendee[]>([])
  const [isLoadingAttendees, setIsLoadingAttendees] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [location, setLocation] = useState('')
  const [isVirtual, setIsVirtual] = useState(false)
  const [virtualUrl, setVirtualUrl] = useState('')
  const [capacity, setCapacity] = useState<number | ''>('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const loadEvents = async () => {
    if (!currentOrganization || !profile || !user) {
      setEvents([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const result = await webEventRepository.listVisible(
        createWebTenantContext({
          authUid: user.uid,
          personId: profile.id,
          organizationId: currentOrganization.id,
          membershipId: currentMembership?.id,
        }),
        { limit: 100 },
      )
      setEvents(result)
      setRegisteredIds(new Set(result.filter((event) => event.isRegistered).map((event) => event.id)))
    } catch (error) {
      console.error('Error al cargar eventos:', error)
      setEvents([])
    } finally {
      setIsLoading(false)
    }
  }

  // Handle URL deep link (e.g. #/events?id=123 from Dashboard "Ver" button)
  const targetEventId = searchParams.get('id') || searchParams.get('eventId')
  useEffect(() => {
    if (!targetEventId || events.length === 0) return
    const found = events.find((e) => e.id === targetEventId)
    if (found) {
      setSelectedEventForDetail(found)
    }
    // Consume the deep link once, so later reloads of `events` do not reopen the modal.
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('id')
        next.delete('eventId')
        return next
      },
      { replace: true },
    )
  }, [targetEventId, events, setSearchParams])

  const handleOpenCreateModal = () => {
    setTitle('')
    setDescription('')
    setLocation('')
    setIsVirtual(false)
    setVirtualUrl('')
    setCapacity('')
    setStartDate('')
    setEndDate('')
    setShowCreateModal(true)
  }

  const handleOpenEditModal = (event: CalendarEvent) => {
    setEditingEvent(event)
    setTitle(event.title)
    setDescription(event.description || '')
    setLocation(event.location || '')
    setIsVirtual(event.isVirtual || false)
    setVirtualUrl(event.virtualUrl || '')
    setCapacity(event.capacity || '')
    setStartDate(toInputDateTimeLocal(event.startDate))
    setEndDate(toInputDateTimeLocal(event.endDate))
    if (selectedEventForDetail?.id === event.id) {
      setSelectedEventForDetail(null)
    }
  }

  const loadAttendees = async (eventId: string) => {
    setIsLoadingAttendees(true)
    try {
      const client = getSupabaseClient()
      const { data, error } = await client
        .from('event_registrations')
        .select(`
          id,
          status,
          checked_in_at,
          membership:memberships (
            id,
            display_name
          )
        `)
        .eq('event_id', eventId)
        .in('status', ['registered', 'checked_in'])

      if (error) throw error
      setAttendees((data || []) as unknown as Attendee[])
    } catch (error) {
      console.error('Error al cargar asistentes:', error)
      setAttendees([])
    } finally {
      setIsLoadingAttendees(false)
    }
  }

  const handleOpenAttendeesModal = (event: CalendarEvent) => {
    setViewingAttendeesEvent(event)
    void loadAttendees(event.id)
  }

  const handleShareWhatsAppAttendees = () => {
    if (!viewingAttendeesEvent) return

    const eventTitle = viewingAttendeesEvent.title
    const dateStr = formatChileanDate(viewingAttendeesEvent.startDate)
    const timeStr = formatChileanTime(viewingAttendeesEvent.startDate)
    const locStr = viewingAttendeesEvent.isVirtual
      ? 'Virtual / Online'
      : (viewingAttendeesEvent.location || 'Por definir')

    const attendeeList =
      attendees.length > 0
        ? attendees
            .map(
              (a, idx) =>
                `${idx + 1}. ${a.membership?.display_name || 'Miembro'} - [${
                  a.status === 'checked_in' ? 'Presente' : 'Inscrito'
                }]`,
            )
            .join('\n')
        : 'Sin inscritos aun.'

    const orgLine = currentOrganization?.name ? `\n*Organizacion:* ${currentOrganization.name}` : ''

    const message = [
      `*LISTA DE ASISTENTES*`,
      `*Actividad:* ${eventTitle}${orgLine}`,
      `*Fecha:* ${dateStr} a las ${timeStr}`,
      `*Lugar:* ${locStr}`,
      `*Total inscritos:* ${attendees.length}`,
      ``,
      `*Nomina:*`,
      attendeeList,
      ``,
      `_Generado desde Zetyar Nexo_`,
    ].join('\n')

    const url = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleShareWhatsAppEvent = (event: CalendarEvent) => {
    const eventTitle = event.title
    const startStr = formatChileanDateTime(event.startDate)
    const endStr = formatChileanDateTime(event.endDate)
    const locStr = event.isVirtual
      ? `Virtual / Online (${event.virtualUrl || 'Por definir'})`
      : (event.location || 'Por definir')
    const mapLink = (!event.isVirtual && (event.location || event.virtualUrl))
      ? `\n*Mapa:* ${getExternalMapUrl(event.location, event.virtualUrl)}`
      : ''
    const descStr = event.description ? `\n*Detalles:* ${event.description}` : ''
    const orgName = currentOrganization?.name ? `\n*Organización:* ${currentOrganization.name}` : ''

    const message = [
      `*INVITACIÓN A ACTIVIDAD*`,
      `*Evento:* ${eventTitle}${orgName}`,
      `*Inicio:* ${startStr}`,
      `*Término:* ${endStr}`,
      `*Lugar:* ${locStr}${mapLink}`,
      descStr,
      ``,
      `_Generado desde Zetyar Nexo_`,
    ].filter(Boolean).join('\n')

    const url = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      if (!isMounted) return
      await loadEvents()
    }

    void run()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const handleRegister = async (eventId: string) => {
    if (!currentOrganization || !profile || !user) return

    setRegisteringId(eventId)
    try {
      if (webEventRepository.register) {
        await webEventRepository.register(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          eventId,
        )
        setRegisteredIds((current) => new Set(current).add(eventId))
      }
    } catch (error) {
      console.error('Error al inscribirse al evento:', error)
      if (String(error).includes('ALREADY_EXISTS')) {
        setRegisteredIds((current) => new Set(current).add(eventId))
      }
    } finally {
      setRegisteringId(null)
    }
  }

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization || !profile || !user) return

    try {
      if (webEventRepository.create) {
        await webEventRepository.create(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          {
            title,
            description: description || undefined,
            location: isVirtual ? undefined : (location || undefined),
            isVirtual,
            virtualUrl: virtualUrl || undefined,
            capacity: capacity ? Number(capacity) : undefined,
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
          },
        )
        await loadEvents()
      }
    } catch (error) {
      console.error('Error al crear el evento en Supabase:', error)
    }

    setShowCreateModal(false)
    setTitle('')
    setDescription('')
    setLocation('')
    setIsVirtual(false)
    setVirtualUrl('')
    setCapacity('')
    setStartDate('')
    setEndDate('')
  }

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization || !profile || !user || !editingEvent) return

    try {
      if (webEventRepository.update) {
        await webEventRepository.update(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          editingEvent.id,
          {
            title,
            description: description || undefined,
            location: isVirtual ? undefined : (location || undefined),
            isVirtual,
            virtualUrl: virtualUrl || undefined,
            capacity: capacity ? Number(capacity) : undefined,
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
          },
        )
        await loadEvents()
      }
    } catch (error) {
      console.error('Error al actualizar la actividad en Supabase:', error)
    }

    setEditingEvent(null)
    setTitle('')
    setDescription('')
    setLocation('')
    setIsVirtual(false)
    setVirtualUrl('')
    setCapacity('')
    setStartDate('')
    setEndDate('')
  }

  const handleDelete = (eventId: string) => {
    guard(async () => {
      if (!currentOrganization || !profile || !user || !webEventRepository.delete) return

      try {
        await webEventRepository.delete(
          createWebTenantContext({
            authUid: user.uid,
            personId: profile.id,
            organizationId: currentOrganization.id,
            membershipId: currentMembership?.id,
          }),
          eventId,
        )
        setEvents((current) => current.filter((item) => item.id !== eventId))
        if (selectedEventForDetail?.id === eventId) {
          setSelectedEventForDetail(null)
        }
      } catch (error) {
        console.error('Error al eliminar la actividad:', error)
      }
    })
  }

  // Calendar navigation helpers
  const handlePrevDate = () => {
    const newDate = new Date(calendarDate)
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1)
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7)
    }
    setCalendarDate(newDate)
  }

  const handleNextDate = () => {
    const newDate = new Date(calendarDate)
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1)
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7)
    }
    setCalendarDate(newDate)
  }

  const handleToday = () => {
    setCalendarDate(new Date())
  }

  // Formatted headers for current period
  const monthTitle = calendarDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })
  const weekDays = getWeekDays(calendarDate)
  const weekStartStr = weekDays[0] ? weekDays[0].toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }) : ''
  const weekEndStr = weekDays[6] ? weekDays[6].toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

  // Find events for a given day
  const getEventsForDay = (date: Date) => {
    return events.filter((e) => isSameDay(new Date(e.startDate), date))
  }

  const today = new Date()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Actividades</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Eventos y actividades de {currentOrganization?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={handleOpenCreateModal}
            >
              Crear actividad
            </Button>
          )}
        </div>
      </div>

      {/* Main View Card */}
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-neutral-100 dark:border-neutral-800 pb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-primary-600" />
            <CardTitle className="text-base capitalize">
              {viewMode === 'list' && 'Próximas actividades'}
              {viewMode === 'month' && monthTitle}
              {viewMode === 'week' && `Semana ${weekStartStr} - ${weekEndStr}`}
            </CardTitle>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Date navigation buttons for Month & Week views */}
            {viewMode !== 'list' && (
              <div className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-xl border border-neutral-200/60 dark:border-neutral-700/60">
                <button
                  type="button"
                  onClick={handlePrevDate}
                  title={viewMode === 'month' ? 'Mes anterior' : 'Semana anterior'}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-700 hover:shadow-xs transition-all"
                >
                  <ChevronLeft className="w-4 h-4 text-neutral-600 dark:text-neutral-300" />
                  <span className="hidden sm:inline">Anterior</span>
                </button>
                <button
                  type="button"
                  onClick={handleToday}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white dark:bg-neutral-900 text-primary-600 dark:text-primary-400 shadow-xs hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all"
                >
                  Hoy
                </button>
                <button
                  type="button"
                  onClick={handleNextDate}
                  title={viewMode === 'month' ? 'Mes siguiente' : 'Semana siguiente'}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-neutral-700 dark:text-neutral-200 hover:bg-white dark:hover:bg-neutral-700 hover:shadow-xs transition-all"
                >
                  <span className="hidden sm:inline">Siguiente</span>
                  <ChevronRight className="w-4 h-4 text-neutral-600 dark:text-neutral-300" />
                </button>
              </div>
            )}

            {/* View Mode Switcher */}
            <div className="flex items-center bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-neutral-900 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                Lista
              </button>
              <button
                type="button"
                onClick={() => setViewMode('week')}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'week'
                    ? 'bg-white dark:bg-neutral-900 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Semana
              </button>
              <button
                type="button"
                onClick={() => setViewMode('month')}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'month'
                    ? 'bg-white dark:bg-neutral-900 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Mes
              </button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse text-center py-8">
              Cargando actividades desde Supabase...
            </p>
          ) : (
            <>
              {/* ===== VISTA 1: LISTA ===== */}
              {viewMode === 'list' && (
                <div>
                  {events.length === 0 ? (
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 py-6 text-center">
                      No hay actividades reales cargadas en {currentOrganization?.name}.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {events.map((event) => {
                        const isRegistered = registeredIds.has(event.id)
                        const isEdited = Boolean(
                          event.updatedAt &&
                            event.createdAt &&
                            new Date(event.updatedAt).getTime() - new Date(event.createdAt).getTime() > 1000,
                        )

                        return (
                          <div
                            key={event.id}
                            className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 hover:border-primary-300 dark:hover:border-primary-800/60 transition-all space-y-3 bg-white dark:bg-neutral-900/50 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div
                                className="cursor-pointer group flex-1"
                                onClick={() => setSelectedEventForDetail(event)}
                              >
                                <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors flex items-center gap-2">
                                  {event.title}
                                  <Info className="w-3.5 h-3.5 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </h3>
                                {event.description && (
                                  <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                                    {event.description}
                                  </p>
                                )}
                              </div>
                              <Badge variant={event.status === 'published' ? 'primary' : 'neutral'}>
                                {event.status}
                              </Badge>
                            </div>

                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
                                <span className="flex items-center gap-1 font-medium text-neutral-700 dark:text-neutral-300">
                                  <Calendar className="w-3.5 h-3.5 text-primary-600" />
                                  {formatChileanDateTime(event.startDate)}
                                </span>
                                {event.isVirtual ? (
                                  <span className="flex items-center gap-1 text-primary-600 dark:text-primary-400 font-medium">
                                    <Video className="w-3.5 h-3.5" /> Virtual
                                  </span>
                                ) : event.location ? (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5" /> {event.location}
                                  </span>
                                ) : null}
                                {!event.isVirtual && (event.location || event.virtualUrl) && (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-semibold focus:outline-none transition-colors"
                                    onClick={() => setExpandedMapEventId(expandedMapEventId === event.id ? null : event.id)}
                                  >
                                    <Map className="w-3 h-3" />
                                    {expandedMapEventId === event.id ? 'Ocultar mapa' : 'Ver mapa'}
                                  </button>
                                )}
                                {event.capacity && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3.5 h-3.5" /> Cupo {event.capacity}
                                  </span>
                                )}
                                {isEdited && (
                                  <span className="italic text-neutral-400">
                                    (Editado el {formatChileanDateTime(event.updatedAt!)})
                                  </span>
                                )}
                              </div>

                              {event.isVirtual && event.virtualUrl && (
                                <div className="text-xs">
                                  <a
                                    href={event.virtualUrl.startsWith('http') ? event.virtualUrl : `https://${event.virtualUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-50 hover:bg-primary-100 dark:bg-primary-950/40 dark:hover:bg-primary-950/70 font-semibold text-primary-700 dark:text-primary-300 transition-colors"
                                  >
                                    <Video className="w-3.5 h-3.5" /> Unirse a la reunión virtual
                                  </a>
                                </div>
                              )}

                              {expandedMapEventId === event.id && (event.location || event.virtualUrl) && (
                                <div className="overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 p-2 space-y-2">
                                  <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 px-1">
                                    <span className="truncate max-w-[70%] font-medium">{event.location || 'Ubicación'}</span>
                                    <a
                                      href={getExternalMapUrl(event.location, event.virtualUrl)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-primary-600 hover:underline font-semibold"
                                    >
                                      <ExternalLink className="w-3 h-3" /> Abrir en Google Maps
                                    </a>
                                  </div>
                                  <iframe
                                    src={getMapEmbedUrl(event.location, event.virtualUrl)}
                                    className="w-full h-56 rounded border-0 bg-white"
                                    allowFullScreen
                                    loading="lazy"
                                  />
                                </div>
                              )}
                            </div>

                            <div className="pt-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleOpenAttendeesModal(event)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 shadow-2xs transition-all cursor-pointer"
                                >
                                  <Users className="w-3.5 h-3.5 shrink-0" />
                                  <span>Ver asistentes</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSelectedEventForDetail(event)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-transparent hover:border-neutral-200 dark:hover:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 transition-all cursor-pointer"
                                >
                                  <Info className="w-3.5 h-3.5 shrink-0" />
                                  <span>Ver información</span>
                                </button>
                              </div>

                              <div className="flex items-center gap-2 self-start sm:self-auto">
                                {isRegistered ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 px-3 py-1.5 rounded-lg shadow-2xs">
                                    <Check className="w-3.5 h-3.5 shrink-0 text-emerald-600" /> Inscrito
                                  </span>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    isLoading={registeringId === event.id}
                                    onClick={() => handleRegister(event.id)}
                                    className="text-xs h-8 px-4"
                                  >
                                    Inscribirme
                                  </Button>
                                )}

                                {canEdit && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    leftIcon={<Pencil className="w-3.5 h-3.5" />}
                                    onClick={() => handleOpenEditModal(event)}
                                  />
                                )}

                                {canDelete && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    leftIcon={<Trash2 className="w-3.5 h-3.5 text-rose-500" />}
                                    onClick={() => handleDelete(event.id)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ===== VISTA 2: MENSUAL ===== */}
              {viewMode === 'month' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-7 gap-1 text-center font-semibold text-xs text-neutral-500 dark:text-neutral-400 py-2 border-b border-neutral-100 dark:border-neutral-800">
                    {WEEKDAY_FULL_NAMES.map((name, i) => (
                      <div key={name} className="truncate">
                        <span className="hidden sm:inline">{name}</span>
                        <span className="sm:hidden">{WEEKDAY_NAMES[i]}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1">
                    {getMonthCalendarDays(calendarDate).map((day, index) => {
                      const isCurrentMonth = day.getMonth() === calendarDate.getMonth()
                      const isDayToday = isSameDay(day, today)
                      const dayEvents = getEventsForDay(day)

                      return (
                        <div
                          key={index}
                          className={`min-h-[90px] sm:min-h-[110px] p-1.5 rounded-lg border transition-colors flex flex-col justify-between ${
                            isCurrentMonth
                              ? 'bg-white dark:bg-neutral-900 border-neutral-100 dark:border-neutral-800/80'
                              : 'bg-neutral-50/50 dark:bg-neutral-950/40 border-neutral-100/50 dark:border-neutral-900/40 text-neutral-300 dark:text-neutral-600'
                          } ${isDayToday ? 'ring-2 ring-primary-500/50' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                                isDayToday
                                  ? 'bg-primary-600 text-white shadow-sm'
                                  : isCurrentMonth
                                    ? 'text-neutral-700 dark:text-neutral-300'
                                    : 'text-neutral-400 dark:text-neutral-600'
                              }`}
                            >
                              {day.getDate()}
                            </span>
                            {dayEvents.length > 0 && (
                              <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 sm:hidden">
                                • {dayEvents.length}
                              </span>
                            )}
                          </div>

                          <div className="space-y-1 mt-1 flex-1 overflow-y-auto max-h-[70px] pr-0.5">
                            {dayEvents.map((evt) => {
                              const timeStr = formatChileanTime(evt.startDate)
                              return (
                                <button
                                  key={evt.id}
                                  type="button"
                                  onClick={() => setSelectedEventForDetail(evt)}
                                  className="w-full text-left px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-950/70 hover:bg-primary-100 dark:hover:bg-primary-900/80 border border-primary-100/60 dark:border-primary-800/50 text-primary-700 dark:text-primary-300 text-[11px] font-medium truncate block transition-all shadow-2xs"
                                  title={`${evt.title} (${timeStr})`}
                                >
                                  <span className="font-semibold mr-1">{timeStr}</span>
                                  {evt.title}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* ===== VISTA 3: SEMANAL ===== */}
              {viewMode === 'week' && (
                <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                  {weekDays.map((day, idx) => {
                    const isDayToday = isSameDay(day, today)
                    const dayEvents = getEventsForDay(day)

                    return (
                      <div
                        key={idx}
                        className={`rounded-xl border p-3 flex flex-col min-h-[220px] transition-all ${
                          isDayToday
                            ? 'bg-primary-50/20 dark:bg-primary-950/20 border-primary-300 dark:border-primary-800'
                            : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800'
                        }`}
                      >
                        <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-2 mb-2">
                          <div>
                            <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase block">
                              {WEEKDAY_FULL_NAMES[idx]}
                            </span>
                            <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
                              {day.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                          {isDayToday && (
                            <Badge variant="primary" className="text-[10px] px-1.5 py-0.5">
                              Hoy
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-2 flex-1">
                          {dayEvents.length === 0 ? (
                            <p className="text-[11px] text-neutral-400 dark:text-neutral-500 italic text-center py-6">
                              Sin actividades
                            </p>
                          ) : (
                            dayEvents.map((evt) => {
                              const timeStr = formatChileanTime(evt.startDate)
                              const isRegistered = registeredIds.has(evt.id)
                              return (
                                <div
                                  key={evt.id}
                                  onClick={() => setSelectedEventForDetail(evt)}
                                  className="p-2.5 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-950/50 hover:bg-primary-100 dark:hover:bg-primary-900/60 cursor-pointer transition-all space-y-1.5 group shadow-2xs"
                                >
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="text-[11px] font-bold text-primary-700 dark:text-primary-300 flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {timeStr}
                                    </span>
                                    {isRegistered && (
                                      <span className="w-2 h-2 rounded-full bg-emerald-500" title="Inscrito" />
                                    )}
                                  </div>
                                  <h4 className="text-xs font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 line-clamp-2">
                                    {evt.title}
                                  </h4>
                                  <div className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                                    {evt.isVirtual ? (
                                      <span className="flex items-center gap-1 text-primary-600">
                                        <Video className="w-2.5 h-2.5" /> Online
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-2.5 h-2.5" /> {evt.location || 'Presencial'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )
                            })
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ===== MODAL DE DETALLE COMPLETO DE LA ACTIVIDAD ===== */}
      <Modal
        isOpen={Boolean(selectedEventForDetail)}
        onClose={() => setSelectedEventForDetail(null)}
        title={selectedEventForDetail?.title || 'Detalles de la actividad'}
      >
        {selectedEventForDetail && (
          <div className="space-y-5">
            {/* Fechas y modalidad en Horario Chileno */}
            <div className="p-3.5 rounded-xl bg-neutral-50 dark:bg-neutral-900/80 border border-neutral-200/80 dark:border-neutral-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Horario de la actividad
                </span>
                <Badge variant={selectedEventForDetail.status === 'published' ? 'primary' : 'neutral'}>
                  {selectedEventForDetail.status}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2 text-neutral-800 dark:text-neutral-200">
                  <Clock className="w-4 h-4 text-primary-600 shrink-0" />
                  <div>
                    <span className="text-neutral-400 block text-[10px]">Inicio</span>
                    <span className="font-semibold">
                      {formatChileanDateTime(selectedEventForDetail.startDate)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-neutral-800 dark:text-neutral-200">
                  <Clock className="w-4 h-4 text-neutral-400 shrink-0" />
                  <div>
                    <span className="text-neutral-400 block text-[10px]">Término</span>
                    <span className="font-semibold">
                      {formatChileanDateTime(selectedEventForDetail.endDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Descripción */}
            {selectedEventForDetail.description && (
              <div className="space-y-1">
                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Descripción
                </span>
                <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-line leading-relaxed">
                  {selectedEventForDetail.description}
                </p>
              </div>
            )}

            {photoContext && (
              <EventPhotosSection
                context={photoContext}
                eventId={selectedEventForDetail.id}
                eventTitle={selectedEventForDetail.title}
                canManage={canManagePhotos}
              />
            )}

            {/* Modalidad & Ubicación / Mapa */}
            <div className="space-y-2">
              <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {selectedEventForDetail.isVirtual ? 'Enlace Virtual' : 'Ubicación y Mapa'}
              </span>

              {selectedEventForDetail.isVirtual ? (
                <div className="p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-950/40 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-primary-700 dark:text-primary-300">
                    <Video className="w-4 h-4" /> Actividad Online
                  </div>
                  {selectedEventForDetail.virtualUrl && (
                    <a
                      href={
                        selectedEventForDetail.virtualUrl.startsWith('http')
                          ? selectedEventForDetail.virtualUrl
                          : `https://${selectedEventForDetail.virtualUrl}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="sm" variant="primary" leftIcon={<ExternalLink className="w-3.5 h-3.5" />}>
                        Unirse a la reunión
                      </Button>
                    </a>
                  )}
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 p-2.5 space-y-2.5">
                  <div className="flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300 px-1">
                    <span className="font-semibold flex items-center gap-1.5 truncate max-w-[70%]">
                      <MapPin className="w-4 h-4 text-primary-600 shrink-0" />
                      {selectedEventForDetail.location || 'Ubicación presencial'}
                    </span>
                    <a
                      href={getExternalMapUrl(selectedEventForDetail.location, selectedEventForDetail.virtualUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary-600 hover:underline font-semibold text-xs"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Abrir en Google Maps
                    </a>
                  </div>
                  <iframe
                    src={getMapEmbedUrl(selectedEventForDetail.location, selectedEventForDetail.virtualUrl)}
                    className="w-full h-56 rounded-lg border-0 bg-white shadow-inner"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              )}
            </div>

            {/* Cupo */}
            {selectedEventForDetail.capacity && (
              <div className="flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                <Users className="w-4 h-4 text-primary-600" />
                <span>Capacidad máxima: <strong>{selectedEventForDetail.capacity} asistentes</strong></span>
              </div>
            )}

            {/* Botones de acción del modal */}
            <div className="pt-4 border-t border-neutral-100 dark:border-neutral-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleShareWhatsAppEvent(selectedEventForDetail)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-[#25D366] hover:bg-[#20bd5a] active:bg-[#1da851] shadow-xs transition-all cursor-pointer"
                  title="Compartir actividad por WhatsApp"
                >
                  <svg className="w-3.5 h-3.5 fill-current shrink-0" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                  <span>Compartir</span>
                </button>

                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Users className="w-3.5 h-3.5" />}
                  onClick={() => {
                    handleOpenAttendeesModal(selectedEventForDetail)
                  }}
                >
                  Ver asistentes
                </Button>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Pencil className="w-3.5 h-3.5" />}
                    onClick={() => handleOpenEditModal(selectedEventForDetail)}
                  >
                    Editar
                  </Button>
                )}
                {canDelete && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Trash2 className="w-3.5 h-3.5 text-rose-500" />}
                    onClick={() => handleDelete(selectedEventForDetail.id)}
                  >
                    Eliminar
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {registeredIds.has(selectedEventForDetail.id) ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 px-3.5 py-1.5 rounded-xl">
                    <Check className="w-4 h-4 text-emerald-600" /> Ya estás inscrito
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    isLoading={registeringId === selectedEventForDetail.id}
                    onClick={() => handleRegister(selectedEventForDetail.id)}
                  >
                    Inscribirme
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setSelectedEventForDetail(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ===== MODAL DE CREACIÓN ===== */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Crear nueva actividad"
      >
        <form onSubmit={handleCreateEvent} className="space-y-4">
          <Input
            label="Título de la actividad"
            placeholder="Ej: Fiestas Patrias, Asamblea General..."
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div>
            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
              Descripción (opcional)
            </label>
            <textarea
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Ej: Detalles de la asamblea, itinerario..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="create-isVirtual"
              className="rounded text-primary-600 focus:ring-primary-500"
              checked={isVirtual}
              onChange={(e) => setIsVirtual(e.target.checked)}
            />
            <label htmlFor="create-isVirtual" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 cursor-pointer">
              Esta actividad es online / virtual
            </label>
          </div>

          {isVirtual ? (
            <Input
              label="Enlace de la reunión (Zoom, Meet, etc.)"
              placeholder="https://zoom.us/j/..."
              value={virtualUrl}
              onChange={(e) => setVirtualUrl(e.target.value)}
            />
          ) : (
            <>
              <Input
                label="Lugar o Dirección (opcional)"
                placeholder="Ej: Av. Chicureo 14100, Colina (o Casa Max, Chicureo)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <Input
                label="Enlace de Google Maps (opcional)"
                placeholder="https://www.google.com/maps/place/... o enlace corto"
                value={virtualUrl}
                onChange={(e) => setVirtualUrl(e.target.value)}
              />
            </>
          )}

          <Input
            label="Cupo / Capacidad máxima (opcional)"
            type="number"
            placeholder="Ej: 50"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value ? Number(e.target.value) : '')}
          />

          <Input
            label="Inicio"
            type="datetime-local"
            required
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Termino"
            type="datetime-local"
            required
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Guardar actividad
            </Button>
          </div>
        </form>
      </Modal>

      {/* ===== MODAL DE EDICIÓN ===== */}
      <Modal
        isOpen={Boolean(editingEvent)}
        onClose={() => setEditingEvent(null)}
        title="Editar actividad"
      >
        <form onSubmit={handleUpdateEvent} className="space-y-4">
          <Input
            label="Título de la actividad"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div>
            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
              Descripción (opcional)
            </label>
            <textarea
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit-isVirtual"
              className="rounded text-primary-600 focus:ring-primary-500"
              checked={isVirtual}
              onChange={(e) => setIsVirtual(e.target.checked)}
            />
            <label htmlFor="edit-isVirtual" className="text-sm font-medium text-neutral-700 dark:text-neutral-300 cursor-pointer">
              Esta actividad es online / virtual
            </label>
          </div>

          {isVirtual ? (
            <Input
              label="Enlace de la reunión (Zoom, Meet, etc.)"
              placeholder="https://zoom.us/j/..."
              value={virtualUrl}
              onChange={(e) => setVirtualUrl(e.target.value)}
            />
          ) : (
            <>
              <Input
                label="Lugar o Dirección (opcional)"
                placeholder="Ej: Av. Chicureo 14100, Colina (o Casa Max, Chicureo)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <Input
                label="Enlace de Google Maps (opcional)"
                placeholder="https://www.google.com/maps/place/... o enlace corto"
                value={virtualUrl}
                onChange={(e) => setVirtualUrl(e.target.value)}
              />
            </>
          )}

          <Input
            label="Cupo / Capacidad máxima (opcional)"
            type="number"
            placeholder="Ej: 50"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value ? Number(e.target.value) : '')}
          />

          <Input
            label="Inicio"
            type="datetime-local"
            required
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Termino"
            type="datetime-local"
            required
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setEditingEvent(null)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Guardar cambios
            </Button>
          </div>
        </form>
      </Modal>

      {/* ===== MODAL DE ASISTENTES ===== */}
      <Modal
        isOpen={Boolean(viewingAttendeesEvent)}
        onClose={() => setViewingAttendeesEvent(null)}
        title={`Asistentes - ${viewingAttendeesEvent?.title || ''}`}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-2">
            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">
              Total Inscritos:
            </span>
            <Badge variant="primary">
              {attendees.length}
            </Badge>
          </div>

          {isLoadingAttendees ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse text-center py-4">
              Cargando lista de asistentes...
            </p>
          ) : attendees.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">
              No hay asistentes inscritos aún en esta actividad.
            </p>
          ) : (
            <div className="max-h-60 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800 pr-1">
              {attendees.map((attendee) => (
                <div key={attendee.id} className="py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary-500 to-[#8e67ff] text-[10px] font-extrabold text-white flex items-center justify-center">
                      {attendee.membership?.display_name?.slice(0, 2).toUpperCase() || 'M'}
                    </div>
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                      {attendee.membership?.display_name || 'Miembro'}
                    </span>
                  </div>
                  <Badge variant={attendee.status === 'checked_in' ? 'primary' : 'neutral'}>
                    {attendee.status === 'checked_in' ? 'Presente' : 'Inscrito'}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
            <button
              type="button"
              disabled={attendees.length === 0}
              onClick={handleShareWhatsAppAttendees}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold text-white bg-[#25D366] hover:bg-[#20bd5a] active:bg-[#1da851] disabled:opacity-50 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
              title={attendees.length === 0 ? 'No hay inscritos para compartir' : 'Compartir lista por WhatsApp'}
            >
              <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
              </svg>
              <span>Enviar a WhatsApp</span>
            </button>

            <Button variant="outline" onClick={() => setViewingAttendeesEvent(null)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {reauthModal}
    </div>
  )
}
